<template>
  <!-- Added v-touch-swipe to catch user gestures globally -->
  <q-layout view="lHh Lpr lFf" v-touch-swipe.mouse="handleSwipe">
    <!-- Added v-model="headerVisible" to manually toggle the header on and off -->
    <q-header
      v-model="headerVisible"
      elevated
      class="bg-primary text-white q-safe-area-top q-safe-area-left q-safe-area-right"
    >
      <q-toolbar>
        <q-btn flat dense round icon="menu" aria-label="Menu" @click="toggleLeftDrawer" />
        <q-btn
          flat
          dense
          round
          icon="info"
          aria-label="Toggle Scoreboard"
          @click="toggleScoreboard"
        />
        <!-- HUD TEXT -->
        <div
          v-if="gameStore.activeGameSummary && route.meta.showHud !== false"
          class="q-ml-md text-subtitle2 text-weight-bold row items-center"
        >
          {{ inningString }}
          <span class="text-uppercase q-ml-md q-mr-sm">
            AB {{ '#' + gameStore.currentBatter?.player?.uniformNumber || '-' }}
            ({{ gameStore.currentBatter?.battingOrder || 1 }} of {{ gameStore.totalBatters }})
          </span>
          <span>
            : {{ gameStore.currentAbState().balls }} - {{ gameStore.currentAbState().strikes }} - {{ gameStore.currentOuts }}
          </span>
          <!-- NEW: Unified Status, Count, and Outs -->
          <span class="text-amber q-ml-md" v-if="gameStore.currentAbState(false).status">
            {{ gameStore.currentAbState(false).status }}
          </span>
        </div>
        <q-space />
        <q-btn
          flat
          dense
          round
          :icon="helpModeEnabled ? 'help' : 'help_outline'"
          :color="helpModeEnabled ? 'amber' : undefined"
          aria-label="Toggle Help Hints"
          @click="toggleHelpMode"
        />
      </q-toolbar>
      <!-- INJECTED HUD: Only displays when an active game is being tracked and we are NOT on the Spray Chart -->
      <GameScoreboardHud
        v-if="showScoreboard && gameStore.activeGameSummary && route.meta.showHud !== false"
      />
    </q-header>

    <q-drawer v-model="leftDrawerOpen" show-if-above bordered class="bg-grey-1">
      <q-list>
        <q-item-label header> Menu </q-item-label>

        <q-item
          v-for="item in dynamicMenuItems"
          :key="item.title"
          clickable
          v-ripple
          :to="item.route"
        >
          <q-item-section avatar>
            <q-icon :name="item.icon" />
          </q-item-section>

          <q-item-section>
            <q-item-label>{{ item.title }}</q-item-label>
            <q-item-label caption v-if="item.caption">
              {{ item.caption }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </q-drawer>

    <q-page-container>
      <router-view />
    </q-page-container>
  </q-layout>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from 'stores/auth-store'
import { useGameStore } from 'stores/game-store'
import GameScoreboardHud from 'components/GameScoreboardHud.vue'
import { useHelpMode } from 'src/composables/useHelpMode'
const { helpModeEnabled, toggleHelpMode } = useHelpMode()

const leftDrawerOpen = ref(false)
const headerVisible = ref(true) // <-- Controls whether the top bar is shown
const showScoreboard = ref(false) // <-- Starts hidden to conserve screen real estate

const route = useRoute()
const authStore = useAuthStore()
const gameStore = useGameStore()

function toggleLeftDrawer() {
  leftDrawerOpen.value = !leftDrawerOpen.value
}

function toggleScoreboard() {
  showScoreboard.value = !showScoreboard.value
}

// Formats the inning number into 'Top 1st', 'Bottom 2nd', etc.
const inningString = computed(() => {
  const inningStr = gameStore.activeInningString||''
  return inningStr.replace('Bottom','Bot')
})

// --- NEW: Global Swipe Handler ---
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleSwipe(details: any) {
  if (details.direction === 'up') {
    // Swiping up pushes the content up and hides the header
    headerVisible.value = false
  } else if (details.direction === 'down') {
    // Swiping down pulls the content down and reveals the header
    headerVisible.value = true
  }
}

// 2. Context-Aware Drawer Logic
// This computed property changes the drawer links based on where the user is
const dynamicMenuItems = computed(() => {
  const items = []

  // If user is not logged in, maybe only show basic info
  if (!authStore.IsUserLoggedIn) {
    return [
      { title: 'Login', icon: 'login', route: '/login', caption: 'Login' },
      { title: 'About', icon: 'info', route: '/about', caption: 'Login' },
    ]
  }

  // CONTEXT: If the user is actively in a game tracking screen
  if (gameStore.activeGameSummary && gameStore.activeGameSummary?.gameId !== '') {
    items.push({
      title: 'Game Tracker',
      icon: 'sports_baseball',
      route: '/GameTracker',
    })
    items.push({ title: 'Edit Lineups', icon: 'list_alt', route: '/SetLineup' })
    items.push({ title: 'Game Summary', icon: 'bar_chart', route: '/GameSummary' })
    items.push({ title: 'Spray Chart', icon: 'bar_chart', route: '/SprayChart' })
  }

  // Always show global settings and logout at the bottom for logged-in users
  items.push({
    title: 'Game Selection',
    icon: 'inventory_2',
    route: '/GameSelection',
  })
  items.push({
    title: 'Database',
    icon: 'storage',
    route: '/DatabaseSync',
  })
  items.push({
    title: 'Settings',
    icon: 'settings_applications',
    route: '/UserSettings',
    caption: 'Settings',
  })
  items.push({ title: 'Logout', icon: 'logout', route: '/logout' })

  return items
})
</script>
